import { FetchHttpHandler, FetchHttpHandlerOptions } from '@smithy/fetch-http-handler';
import { buildQueryString } from '@smithy/querystring-builder';
import { HeaderBag, HttpHandlerOptions } from '@smithy/types';
import { HttpResponse, HttpRequest, } from '@smithy/protocol-http';
import type {ProgressCallback} from './aws-types';
import dbg from "@/common/utils/diagnostics";

// Custom 'fetch handler' from StackOverflow 'How to track upload progress to S3 using aws-sdk V3 for browser (javascript)'
// https://stackoverflow.com/questions/65728325/how-to-track-upload-progress-to-s3-using-aws-sdk-v3-for-browser-javascript

export default class XhrHttpHandler extends FetchHttpHandler {

    onProgressCallbacks: ProgressCallback[] = [];
    myRequestTimeout?: number;

    constructor({ requestTimeout }: FetchHttpHandlerOptions = {}) {
        super({ requestTimeout });
        this.myRequestTimeout = requestTimeout;
    }

    handle(request: HttpRequest, { abortSignal }: HttpHandlerOptions = {}): Promise<{ response: HttpResponse }> {
        // Override requests to post or put, otherwise use default fetch method
        if ((request.method === 'PUT') && request.body) {
            return this.handleByXhr(request, { abortSignal });
        } else {
            return super.handle(request, { abortSignal });
        }
    }

    onProgress(callback: ProgressCallback) {
        this.onProgressCallbacks.push(callback);
    }

    offProgress(callback: ProgressCallback) {
        const index = this.onProgressCallbacks.indexOf(callback);
        if (index !== -1) {
            this.onProgressCallbacks.splice(index, 1);
        }
    }

    /**
     * handles a request by XHR instead of fetch
     * this is a copy the `handle` method of the `FetchHttpHandler` class of @aws-sdk/fetch-http-handler
     * replacing the `Fetch`part with XHR
     */
    async handleByXhr(request: HttpRequest, { abortSignal }: HttpHandlerOptions = {}): Promise<{ response: HttpResponse }> {
        const requestTimeoutInMs = this.myRequestTimeout;

        // if the request was already aborted, prevent doing extra work
        if (abortSignal && abortSignal.aborted) {
            const abortError = new Error('Request aborted');
            abortError.name = 'AbortError';
            return Promise.reject(abortError);
        }

        let path = request.path;
        if (request.query) {
            const queryString = buildQueryString(request.query);
            if (queryString) {
                path += `?${queryString}`;
            }
        }

        const { port, method } = request;
        const url = `${request.protocol}//${request.hostname}${port ? `:${port}` : ''}${path}`;
        // Request constructor doesn't allow GET/HEAD request with body
        // ref: https://github.com/whatwg/fetch/issues/551
        const body = method === 'GET' || method === 'HEAD' ? undefined : request.body;
        const requestOptions = {
            body,
            headers: new Headers(request.headers),
            method,
        };


        const myXHR = new XMLHttpRequest();
        const xhrPromise = new Promise <{ headers: string[], body: Blob, status: number }>((resolve, reject) => {
            try {
                myXHR.responseType = 'blob';

                // bind the events
                myXHR.onload = progressEvent => {
                 
                    /*console.log('XHR.on.load', {
                        url: myXHR.responseURL,
                        status: myXHR.status,
                        text: myXHR.statusText,
                        restype: myXHR.response.type,
                        headers:myXHR.getAllResponseHeaders(),
                        progressEvent,
                        myXHR                        
                    });*/

                    resolve({
                        body: myXHR.response,
                        headers: myXHR.getAllResponseHeaders().split(/[\r\n]+/),
                        status: myXHR.status
                    });
                };
                myXHR.onerror = progressEvent => {
                    //console.log('XHR.on.error', { myXHR, progressEvent });
                
                    let errorMessage = "Unknown error occured while uploading a file";
                    try {
                        errorMessage = myXHR.responseText;
                    } catch { }

                    dbg.captureException(errorMessage, { extra: { myXHR, progressEvent } });

                    reject(new Error(errorMessage));
                };
                myXHR.onabort = progressEvent => {

                    //console.log('XHR.on.abort', { myXHR, progressEvent });

                    dbg.logWarning("Upload aborted", "upload", { extra: { myXHR, progressEvent } });

                    const abortError = new Error('Request aborted');
                    abortError.name = 'AbortError';
                    reject(abortError);
                };

                // progress event must be bound to the `upload` property
                if (myXHR.upload) {
                    myXHR.upload.onprogress = progressEvent => {
                        
                        //console.log('XHR.on.onprogress', progressEvent );
                        this.onProgressCallbacks.forEach(c => c(path, progressEvent));
                    };
                }


                myXHR.open(requestOptions.method, url);
                // append headers
                if (requestOptions.headers) {
                    (requestOptions.headers).forEach((headerVal, headerKey, headers) => {
                        if (['host', 'content-length'].indexOf(headerKey.toLowerCase()) >= 0) {
                            // avoid "refused to set unsafe header" error message
                            return;
                        }

                        myXHR.setRequestHeader(headerKey, headerVal);
                    });
                }
                myXHR.send(requestOptions.body);
            } catch (e) {
                console.error('S3 XHRHandler error', e);
                reject(e);
            }
        });

        const raceOfPromises = [
            xhrPromise.then((response) => {
                const fetchHeaders = response.headers;
                const transformedHeaders:HeaderBag = {};

                fetchHeaders.forEach(header => {
                    const name = header.substr(0, header.indexOf(':'));
                    const val = header.substr(header.indexOf(':') + 1);
                    if (name && val) {
                        transformedHeaders[name] = val.trim();
                    }
                });

                const hasReadableStream = response.body !== undefined;

                // Return the response with buffered body
                if (!hasReadableStream) {
                    return response.body.text().then(body => ({
                        response: new HttpResponse({
                            headers: transformedHeaders,
                            statusCode: response.status,
                            body,
                        }),
                    }));
                }
                // Return the response with streaming body
                return {
                    response: new HttpResponse({
                        
                        headers: transformedHeaders,
                        statusCode: response.status,
                        body: response.body,
                    }),
                };
            }),
            this.requestTimeoutFn(requestTimeoutInMs),
        ];
        if (abortSignal) {
            raceOfPromises.push(
                new Promise<never>((resolve, reject) => {
                    abortSignal.onabort = () => {
                        myXHR.abort();
                    };
                })
            );
        }

      
        //@ts-ignore
        return Promise.race(raceOfPromises);
    }

    requestTimeoutFn(timeoutInMs = 0):Promise<never> {
        return new Promise((resolve, reject) => {
            if (timeoutInMs) {
                setTimeout(() => {
                    const timeoutError = new Error(`Request did not complete within ${timeoutInMs} ms`);
                    timeoutError.name = 'TimeoutError';
                    reject(timeoutError);
                }, timeoutInMs);
            }
        });
    }
}

